import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { JaktomradeResponse } from "../api/types";
import { ComboboxOption } from "../components/Form/Combobox/Combobox";
import AppDataContext from "../contexts/AppDataContext/DataProvider";
import { StorageContext } from "../contexts/StorageContext/StorageProvider";
import { FilterType } from "../pages/Oversikt/Components/FilterCombobox";
import { getJaktdager, getSkutteDyr } from "../pages/Oversikt/helpers";
import { useStateful } from "./useStateful";

export default function useOversikt() {
  const location = useLocation();
  const appData = useContext(AppDataContext);
  const storage = useContext(StorageContext);
  const storageKeyJaktomrade = { name: "oversikt-jaktomrade" };
  const storageKeyFiltre = { name: "oversikt-filtre" };
  const jegerNummer = appData.jegeropplysninger.data?.Jegernummer;

  const jaktomrade = useStateful<ComboboxOption<JaktomradeResponse> | null>(null);
  const valgtOmradekode =
    location.state && "valgtOmradekode" in location.state && location.state.valgtOmradekode;

  const showMissingSelection = !jaktomrade.value || !appData.jaktområder?.data;

  const selectedFilters = useStateful<FilterType[]>(
    storage.get<FilterType[]>(storageKeyFiltre)?.data ?? [FilterType.Elg, FilterType.Hjort],
  );
  const showJaktdagTab = jaktomrade.value?.data?.JaktPåElg || jaktomrade.value?.data?.JaktPåHjort;
  const showArtFilter = jaktomrade.value?.data?.JaktPåElg && jaktomrade.value?.data?.JaktPåHjort;
  const showMineRegOption = !jaktomrade.value?.data?.JaktPåVillrein;

  const totalResultsJaktdager = useStateful<number>(0);
  const totalResultsIndivider = useStateful<number>(0);

  const jaktdager = getJaktdager(
    jaktomrade.value?.id,
    selectedFilters.value,
    storage,
    jegerNummer,
    showArtFilter,
    totalResultsJaktdager,
  );

  const skutteDyr = getSkutteDyr(
    jaktomrade.value?.id,
    selectedFilters.value,
    storage,
    jegerNummer,
    showArtFilter,
    totalResultsIndivider,
  );

  const hasJaktdager =
    jaktdager.data !== undefined &&
    jaktdager.data.length > 0 &&
    jaktdager.data.filter((j) => j.data.Jaktfelt === jaktomrade.value?.id).length > 0 &&
    jaktomrade.value;

  const hasSkutteDyr =
    skutteDyr.data !== undefined &&
    skutteDyr.data.length > 0 &&
    skutteDyr.data.filter((i) =>
      [i.data.Jaktfelt, i.data.Vald, i.data.Villreinvald].includes(jaktomrade.value?.id ?? "none"),
    ).length > 0 &&
    jaktomrade.value;

  const hasJaktdag = (jaktomrade: ComboboxOption<JaktomradeResponse> | null) => {
    return jaktomrade && (jaktomrade.data?.JaktPåElg || jaktomrade.data?.JaktPåHjort);
  };

  useEffect(() => {
    if (appData.sync.didTrySyncWithItems.value && jaktomrade.value?.id) {
      skutteDyr.refetch();
      if (jaktomrade.value.data?.JaktPåHjort || jaktomrade.value.data?.JaktPåElg) {
        jaktdager.refetch();
      }
    }
  }, [appData.sync.didTrySyncWithItems.value]);

  const handleSelectJaktomrade = (selected: ComboboxOption<JaktomradeResponse> | null) => {
    //Ved bytting av jaktområde gjør vi div sjekker for å sette filtre,
    // basert på forrige filtre, og på mulighetene det nye jaktområdet har for filtrering

    if (!selected || !selected?.id) return;
    const filters: FilterType[] = [];

    //Nytt område har jakt på både elg og hjort og sist område filtrerte bare elg,
    // eller hverken elg eller hjort er med i sist filtre -> legg til elg
    ((selected.data?.JaktPåElg &&
      selected.data.JaktPåHjort &&
      selectedFilters.value.includes(FilterType.Elg)) ||
      !(
        selectedFilters.value.includes(FilterType.Elg) ||
        selectedFilters.value.includes(FilterType.Hjort)
      )) &&
      filters.push(FilterType.Elg);

    //Nytt område har jakt på både elg og hjort og sist område filtrerte bare hjort,
    // eller hverken elg eller hjort er med i sist filtre -> legg til hjort
    ((selected.data?.JaktPåElg &&
      selected.data.JaktPåHjort &&
      selectedFilters.value.includes(FilterType.Hjort)) ||
      !(
        selectedFilters.value.includes(FilterType.Elg) ||
        selectedFilters.value.includes(FilterType.Hjort)
      )) &&
      filters.push(FilterType.Hjort);

    //Nytt område har ikke jakt på villrein, bruker filtrerte på 'mine registreringer' på sist område -> legg til mine reg.
    !selected.data?.JaktPåVillrein &&
      selectedFilters.value.includes(FilterType.MineReg) &&
      filters.push(FilterType.MineReg);

    //Bruker filtrerte på registreringer med varsler på sist område -> blir med videre
    selectedFilters.value.includes(FilterType.VarslerReg) && filters.push(FilterType.VarslerReg);

    selectedFilters.set(filters);
    jaktomrade.set(selected);

    //Lagrer i storage, så valg av jaktområde og filtre huskes når bruker navigerer mellom sider
    storage.add({
      objectKey: storageKeyJaktomrade,
      data: selected,
    });

    storage.add({
      objectKey: storageKeyFiltre,
      data: filters,
      operation: "none",
      objectType: "Other",
    });
  };

  const handleFilter = (toggled: FilterType) => {
    const newFilters = selectedFilters.value.includes(toggled)
      ? selectedFilters.value.filter((e) => e !== toggled)
      : [...selectedFilters.value, toggled];

    selectedFilters.set(newFilters);

    storage.add({
      objectKey: storageKeyFiltre,
      data: newFilters,
      operation: "none",
      objectType: "Other",
    });
  };

  useEffect(() => {
    const storedJaktomrade = storage.get<ComboboxOption<JaktomradeResponse>>(storageKeyJaktomrade);
    if (storedJaktomrade && !valgtOmradekode) {
      jaktomrade.set(storedJaktomrade.data);
    }
  }, []);

  useEffect(() => {
    if (jaktomrade.value?.id) {
      skutteDyr.refetch();
      if (jaktomrade.value.data?.JaktPåHjort || jaktomrade.value.data?.JaktPåElg) {
        jaktdager.refetch();
      }
    }
  }, [jaktomrade.value?.id]);

  return {
    loading:
      jaktdager.isFetching || skutteDyr.isFetching || skutteDyr.isLoading || jaktdager.isLoading,
    handleSelectJaktomrade,
    jaktomrade,
    valgtOmradekode,
    showMissingSelection,
    showArtFilter,
    showMineRegOption,
    handleFilter,
    hasJaktdag,
    showJaktdagTab,
    hasJaktdager,
    jaktdager,
    storage,
    hasSkutteDyr,
    skutteDyr,
    location,
    selectedFilters,
    noFilteredResultsJaktdager: jaktdager.data?.length === 0 && totalResultsJaktdager.value != 0,
    noFilteredResultsIndivider: skutteDyr.data?.length === 0 && totalResultsIndivider.value != 0,
  };
}
